// In-browser mic capture. Both batch and streaming (live-transcription) modes now drive the
// same durable-ingest session lifecycle (useStreamingSession, docs/specs/phase1-2.md §2-3):
// captured PCM streams to the bridge as it's recorded (crash-recoverable, resume-on-reconnect —
// docs/adr-004-unified-streaming-ingest.md), and on Stop the backend assembles a playable
// master + (batch) triggers transcription. MediaRecorder + the client-direct S3 multipart
// upload queue are retired for *captured* audio; they remain the path for **imported** files
// (drag-in, handled elsewhere — see App.tsx's Shell) — out of this screen's scope.
import { useEffect, useRef, useState } from 'react';
import { MicrophoneIcon } from '@heroicons/react/24/outline';
import { useStreamingSession } from '../hooks/useStreamingSession';
import type { EngineChoice } from '@scribe/core';
import { useEngineChoices, useFolders, useTags, useVocabularies } from '../lib/queries';
import { formatMs, formatUsd } from '../components/ui';
import { LiveCaptions } from '../components/LiveCaptions';
import { stashPendingCaptions } from '../lib/pendingCaptions';
import { markJustRecorded } from '../lib/justRecorded';
import { useTranscriptionSettings } from '../lib/ondemandQueries';
import { recordErrorProblemCode } from '../lib/streamingErrors';
import { RecordingProblem } from '../components/RecordingProblem';
import { TranscriptionOptions } from '../components/TranscriptionOptions';
import { useTranscriptionPrefs } from '../hooks/useTranscriptionPrefs';
import { useCaptionsWatched } from '../hooks/useCaptionsWatched';
import { useLiveAutoSwitch } from '../hooks/useLiveAutoSwitch';
import { liveAutoSwitchEnabled } from '../lib/featureFlags';

// LIVE-SAVE-1: the "Keep live while away" override is a per-viewer convenience, so browser
// storage is fine — and every access is guarded (private windows / blocked storage throw).
const ALWAYS_LIVE_KEY = 'scribe.liveAlwaysOn';
function readAlwaysLive(): boolean {
  try {
    return window.localStorage.getItem(ALWAYS_LIVE_KEY) === '1';
  } catch {
    return false;
  }
}
function writeAlwaysLive(value: boolean): void {
  try {
    window.localStorage.setItem(ALWAYS_LIVE_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable — the choice still applies for this session
  }
}

export function RecordScreen({ onDone }: { onDone: (recordingId: string) => void }) {
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // AI-3: per-recording opt-out for the automatic post-transcription AI summary. Default
  // checked (on) — matches the server default (on unless the org-level switch is off).
  const [autoSummary, setAutoSummary] = useState(true);

  // recorder.onstop-style refs aren't needed anymore (no MediaRecorder), but Start/Stop still
  // need whatever title/folder/tags are current at the moment they're clicked, not whatever
  // they were when the mode toggle was last touched — fields stay editable until Start.
  const titleRef = useRef(title);
  titleRef.current = title;
  const folderIdRef = useRef(folderId);
  folderIdRef.current = folderId;
  const selectedTagsRef = useRef(selectedTags);
  selectedTagsRef.current = selectedTags;
  const autoSummaryRef = useRef(autoSummary);
  autoSummaryRef.current = autoSummary;

  const { data: folders } = useFolders();
  const { data: tags } = useTags();
  const { data: vocabularies } = useVocabularies();
  // ONDEMAND-1: under an on-access default a saved (batch) recording waits to be transcribed;
  // say so quietly. Live transcription is always immediate server-side.
  const { data: transcriptionSettings } = useTranscriptionSettings();
  const onAccessDefault =
    transcriptionSettings?.feature_enabled === true &&
    transcriptionSettings.effective_default_policy === 'on_access';

  // Batch/stream mode toggle (WEBSTREAM-2). Both now share one lifecycle
  // (useStreamingSession) — only `job_spec.mode` differs (docs/specs/phase1-2.md §2-3).
  const [mode, setMode] = useState<'batch' | 'streaming'>('batch');

  // Language + vocabulary, remembered across recordings (see useTranscriptionPrefs). The hook
  // owns the language filtering, so a remembered vocabulary belonging to the OTHER language
  // resolves to "None" rather than being submitted and rejected by AWS.
  const { language, setLanguage, vocabularyName, setVocabularyName } = useTranscriptionPrefs(
    vocabularies?.items,
  );
  // Same reason as titleRef/folderIdRef above: Start reads whatever is selected at the moment
  // it is clicked, not whatever was selected when the mode toggle was last touched.
  const vocabularyNameRef = useRef(vocabularyName);
  vocabularyNameRef.current = vocabularyName;
  const languageRef = useRef(language);
  languageRef.current = language;
  // WHISPER-1: per-recording engine, offered only when the server lists more than AWS.
  const engines = useEngineChoices();
  const [engine, setEngine] = useState<EngineChoice>('aws');
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const streaming = useStreamingSession();
  // Same reason, one step subtler: `handleStop` reads the caption list AFTER awaiting
  // `streaming.stop()`, but `streaming` is the hook object from the render that created the
  // handler. Segments finalized between that render and the socket closing — which is exactly
  // when the bridge flushes the tail of the session — would be missing from the stash. Mirror
  // it into a ref so the stash gets what the session actually ended with.
  const finalizedRef = useRef(streaming.finalized);
  finalizedRef.current = streaming.finalized;
  const batchPassExpectedRef = useRef(streaming.batchPassExpected);
  batchPassExpectedRef.current = streaming.batchPassExpected;
  const streamingIdle =
    streaming.status === 'idle' || streaming.status === 'done' || streaming.status === 'error';

  // LIVE-SAVE-1 (docs/specs/live-savings-1.md §4): swap to record-only while nobody is
  // watching the captions (hidden tab, captions scrolled away or collapsed), back on return.
  // Flag off: `enabled` is false, the hooks never fire, and none of the extra controls render.
  const autoSwitchOn = liveAutoSwitchEnabled();
  const [captionsCollapsed, setCaptionsCollapsed] = useState(false);
  const [alwaysLive, setAlwaysLiveState] = useState(readAlwaysLive);
  const setAlwaysLive = (value: boolean) => {
    setAlwaysLiveState(value);
    writeAlwaysLive(value);
  };
  const captionsAreaRef = useRef<HTMLDivElement | null>(null);
  const captionsWatched = useCaptionsWatched(captionsAreaRef, {
    collapsed: captionsCollapsed,
    active: autoSwitchOn && streaming.status === 'live',
  });
  const autoSwitch = useLiveAutoSwitch({
    enabled: autoSwitchOn,
    status: streaming.status,
    mode: streaming.mode,
    setMode: streaming.setMode,
    watched: captionsWatched,
    batchPassExpected: streaming.batchPassExpected,
    alwaysLive,
  });

  // WEB-C5-equivalent unload guard: warn before the tab is closed/navigated while a capture
  // session is in flight (connecting/live/stopping), in either mode.
  useEffect(() => {
    const atRisk =
      streaming.status === 'connecting' ||
      streaming.status === 'live' ||
      streaming.status === 'stopping';
    if (!atRisk) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [streaming.status]);

  function handleStart() {
    const fallbackTitle =
      mode === 'batch'
        ? `Mic recording ${new Date().toLocaleString()}`
        : `Live transcription ${new Date().toLocaleString()}`;
    // AI-3: an empty title falls back to a generated placeholder — that's a default title
    // the AI auto-title pass should feel free to replace; a typed title is user-set.
    const titleIsDefault = !titleRef.current.trim();
    void streaming.start(
      {
        title: titleRef.current.trim() || fallbackTitle,
        folderId: folderIdRef.current || null,
        tags: selectedTagsRef.current,
        vocabularyName: vocabularyNameRef.current || null,
        language: languageRef.current,
        engine: engineRef.current,
        titleIsDefault,
        autoSummary: autoSummaryRef.current,
      },
      mode,
    );
  }

  async function handleStop() {
    const id = await streaming.stop();
    if (id) {
      // A batch-authoritative session (the bridge's `run_batch_pass`) lands on the detail
      // screen as `queued`/`transcribing` for 30s+ while the batch pass runs, with nothing on
      // screen. Carry the captions across so the wait isn't a blank gap. A strictly-live,
      // never-resumed session is persisted inline and its real transcript is already there —
      // nothing to carry.
      if (batchPassExpectedRef.current && finalizedRef.current.length > 0) {
        stashPendingCaptions(id, finalizedRef.current);
      }
      // ONDEMAND-1: landing on the detail right after capture is not "opening" it — the detail
      // screen must not auto-transcribe a deferred recording on this visit.
      markJustRecorded(id);
      onDone(id);
    }
  }

  function toggleTag(name: string) {
    setSelectedTags((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name],
    );
  }

  // Metadata fields + the mode toggle only make sense before a capture session has started —
  // once connecting/live/stopping, hide them (not just disable) rather than leave them
  // visible-but-inert.
  const showMetadataFields = streamingIdle;

  return (
    <div className="card">
      <div className="card-head">
        <h1>Record</h1>
      </div>
      <p className="muted">
        Capture audio directly in the browser, then upload it for transcription.
      </p>

      {/* Rendered above the panel, next to the Start/Stop control that produced it: at the
          bottom of a tall record card the failure landed off-screen. `role="alert"` (which
          implies aria-live="assertive" + aria-atomic) on a conditionally-mounted node is the
          case screen readers announce reliably — matching App.tsx's banner precedent. No
          focus-stealing: that would yank a mid-keyboard-navigation user. */}
      {/* ERRORS-1: rendered from the shared catalogue (RecordingProblem) — title, cause,
          numbered steps, and the catalogue's primary action. Restart stays as its own button
          so every failure keeps the one-click way back into a session. */}
      {streaming.status === 'error' && streaming.error && (
        <RecordingProblem
          code={recordErrorProblemCode(streaming.error)}
          recordingId={streaming.recordingId}
          audioCaptured={streaming.captured}
          footnote="Start a new session to keep going."
        >
          <button className="sm" onClick={handleStart} disabled={!streaming.available}>
            Restart
          </button>
        </RecordingProblem>
      )}
      {/* The mic went away mid-session: say so, and offer the clean Stop that keeps what was
          captured. Nothing is torn down automatically (see useStreamingSession.deviceLost). */}
      {streaming.deviceLost && streaming.status === 'live' && (
        <RecordingProblem
          code="capture_device_lost"
          recordingId={streaming.recordingId}
          onPrimaryAction={() => void handleStop()}
        />
      )}

      <div className="record-panel">
        <div className="record-timer">{formatMs(streaming.elapsedMs)}</div>
        <div className="record-controls">
          {streamingIdle && (
            // Disabled (not hidden — e2e/mock.spec.ts asserts visibility) without a configured
            // bridge, so the unreachable-by-design `bridge_not_configured` error stops being
            // one click away in mock mode. Mirrors the Live-transcription chip's treatment.
            <button
              className="btn-rec"
              onClick={handleStart}
              disabled={!streaming.available}
              title={
                streaming.available
                  ? undefined
                  : 'Recording needs a configured capture bridge (unavailable in mock mode).'
              }
            >
              <MicrophoneIcon width={16} height={16} aria-hidden="true" />
              Start recording
            </button>
          )}
          {!streamingIdle && (
            <button
              className="danger"
              onClick={handleStop}
              disabled={streaming.status === 'stopping'}
            >
              Stop
            </button>
          )}
        </div>
        {streaming.reconnecting && <p className="muted">Reconnecting…</p>}
        {!streaming.reconnecting && streaming.status === 'connecting' && (
          <p className="muted">Connecting…</p>
        )}
        {onAccessDefault && (
          <p className="muted ondemand-hint">
            Saved recordings are transcribed when opened, searched or asked about. Live
            transcription is always immediate.
          </p>
        )}
        {!streaming.reconnecting && streaming.status === 'live' && (
          <p className="muted">
            {/* streaming.mode (not the idle-picker's local `mode`) so this reflects a
                mid-session toggle (SWAP-2) instead of staying stuck on whatever was chosen
                before Start. */}
            <span className="rec-dot" /> {streaming.mode === 'batch' ? 'Recording…' : 'Live…'}
          </p>
        )}
      </div>

      {showMetadataFields && (
        <div className="record-review">
          <div className="field">
            <span className="label">Recording mode</span>
            <div className="tags" role="group" aria-label="Recording mode">
              <button
                type="button"
                className={'chip chip-toggle' + (mode === 'batch' ? ' chip-selected' : '')}
                aria-pressed={mode === 'batch'}
                onClick={() => setMode('batch')}
              >
                Record → Upload
              </button>
              <button
                type="button"
                className={'chip chip-toggle' + (mode === 'streaming' ? ' chip-selected' : '')}
                aria-pressed={mode === 'streaming'}
                disabled={!streaming.available}
                title={
                  streaming.available
                    ? undefined
                    : 'Live transcription needs a configured bridge (unavailable in mock mode).'
                }
                onClick={() => setMode('streaming')}
              >
                Live transcription
              </button>
            </div>
          </div>
          <div className="field">
            <span className="label">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled recording"
            />
          </div>
          <div className="field">
            <span className="label">Folder</span>
            <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">No folder</option>
              {folders?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <TranscriptionOptions
            idPrefix="record"
            language={language}
            onLanguageChange={setLanguage}
            vocabularyName={vocabularyName}
            onVocabularyChange={setVocabularyName}
            vocabularies={vocabularies?.items}
            engine={engine}
            onEngineChange={setEngine}
            engines={engines}
          />
          <div className="field">
            <span className="label">Tags</span>
            <div className="tags">
              {tags?.items.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={
                    'chip chip-toggle' + (selectedTags.includes(t.name) ? ' chip-selected' : '')
                  }
                  onClick={() => toggleTag(t.name)}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label className="field-checkbox" htmlFor="ai-summary-toggle">
              <input
                id="ai-summary-toggle"
                type="checkbox"
                checked={autoSummary}
                disabled={!streamingIdle}
                onChange={(e) => setAutoSummary(e.target.checked)}
              />
              AI summary
            </label>
          </div>
        </div>
      )}

      {/* SWAP-2 (docs/specs/phase3-2.md §3): an in-session Live/Batch toggle, wired to
          streaming.setMode — distinct from the idle-only initial-choice toggle above, which
          stays local `mode` state and unchanged. Visible whenever a capture session is in
          flight (connecting/live/stopping), regardless of which mode it's currently in, but
          only INTERACTIVE while `status === 'live'` (finding 3): `useStreamingSession.setMode`
          no-ops while the session is still opening (`sessionRef` is null during
          'connecting'), so rendering the toggle interactive then would flip the UI without
          the toggle ever reaching the session — desyncing the two. During
          'connecting'/'stopping' show the current mode as a non-interactive label instead. */}
      {!streamingIdle && (
        <div className="record-review" ref={captionsAreaRef}>
          <div className="field">
            <span className="label">Transcription</span>
            {streaming.status === 'live' ? (
              <div className="tags" role="group" aria-label="Transcription mode">
                <button
                  type="button"
                  className={
                    'chip chip-toggle' + (streaming.mode === 'batch' ? ' chip-selected' : '')
                  }
                  aria-pressed={streaming.mode === 'batch'}
                  onClick={() => autoSwitch.setModeByUser('batch')}
                >
                  Record only
                </button>
                <button
                  type="button"
                  className={
                    'chip chip-toggle' + (streaming.mode === 'streaming' ? ' chip-selected' : '')
                  }
                  aria-pressed={streaming.mode === 'streaming'}
                  disabled={!streaming.available}
                  title={
                    streaming.available
                      ? undefined
                      : 'Live transcription needs a configured bridge (unavailable in mock mode).'
                  }
                  onClick={() => autoSwitch.setModeByUser('streaming')}
                >
                  Live transcription
                </button>
              </div>
            ) : (
              <p className="muted">
                {streaming.mode === 'batch' ? 'Record only' : 'Live transcription'} —{' '}
                {streaming.status === 'connecting' ? 'connecting…' : 'stopping…'}
              </p>
            )}
          </div>
          {autoSwitchOn && streaming.status === 'live' && (
            <div className="field">
              <div className="tags">
                <button
                  type="button"
                  className="chip chip-toggle"
                  aria-expanded={!captionsCollapsed}
                  onClick={() => setCaptionsCollapsed((v) => !v)}
                >
                  {captionsCollapsed ? 'Show captions' : 'Hide captions'}
                </button>
                <label className="field-checkbox" htmlFor="always-live-toggle">
                  <input
                    id="always-live-toggle"
                    type="checkbox"
                    checked={alwaysLive}
                    onChange={(e) => setAlwaysLive(e.target.checked)}
                  />
                  Keep live while away
                </label>
              </div>
              {autoSwitch.autoSwitched && (
                <p className="muted" role="status">
                  Captions paused while nobody is watching — recording continues. They resume
                  when you come back.
                </p>
              )}
            </div>
          )}
          {streaming.status === 'live' &&
            (autoSwitchOn && captionsCollapsed ? (
              <p className="muted">Captions hidden — recording continues.</p>
            ) : streaming.mode === 'streaming' ? (
              <>
                <p className="muted">Est. cost {formatUsd(streaming.costUsd)} · streaming rate</p>
                <LiveCaptions
                  finalized={streaming.finalized}
                  partial={streaming.partial}
                  status={streaming.status}
                />
              </>
            ) : streaming.finalized.length > 0 || streaming.partial ? (
              // WAVE1-17 (web-ui-issues #17): swapped back to Record-only mid-session but this
              // session already accumulated live captions — batch-authoritative (ADR-004 §4)
              // still means the eventual single batch transcript supersedes these, but yanking
              // them off-screen the instant the toggle flips was bad feedback. Keep them
              // visible, dimmed, until the session ends (streamingIdle hides this whole block).
              <LiveCaptions
                finalized={streaming.finalized}
                partial={streaming.partial}
                status={streaming.status}
                provisional
              />
            ) : (
              <p className="muted">Recording — captions off</p>
            ))}
          {/* Stop is not instant — session.stop() waits on the bridge's finalize (assemble the
              master, then trigger the batch pass). Dropping the captions the moment status
              leaves 'live' left a blank card for that whole window, and then a blank detail
              screen after it. Keep them, dimmed: whichever mode this session ends in, the
              file-authoritative transcript is what actually gets stored. */}
          {streaming.status === 'stopping' &&
            (streaming.finalized.length > 0 || streaming.partial) && (
              <LiveCaptions
                finalized={streaming.finalized}
                partial={streaming.partial}
                status={streaming.status}
                provisional
              />
            )}
        </div>
      )}

    </div>
  );
}
